import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { RightOutlined } from '@ant-design/icons';
import { Checkbox } from 'antd';

import { ICascaderItem, AnyObjType } from '../interface';
import style from './index.module.less';

const defaultFunc = () => {};

interface Prop {
  multiple: boolean;
  value: string | number;
  label: string | number;
  onClick: (value: string | number, label: string | number, deep: number, data: AnyObjType) => void;
  onHover: (value: string | number, children: ICascaderItem[], deep: number) => void;
  children: ICascaderItem[];
  deep: number;
  selected: boolean;
  active: boolean;
  onChange: (
    itemSelected: boolean,
    value: string | number,
    data: AnyObjType,
    deep: number,
    label: string | number
  ) => void;
  indeterminate: boolean;
  itemWidth: number;
  renderNode?: (
    label: string | number,
    value: (string | number)[] | string | number,
    data: AnyObjType
  ) => React.ReactNode;
  // 选中就赋值
  changeOnSelect: boolean;
  data: AnyObjType;
  disabledCheckBox?: boolean;
}

const Item: React.FC<Prop> = ({
  multiple = false,
  value = '',
  label = '',
  onClick = defaultFunc,
  onHover = defaultFunc,
  children = [],
  deep = 0,
  selected = false,
  active = false,
  onChange = defaultFunc,
  indeterminate = false,
  itemWidth = 0,
  renderNode = undefined,
  changeOnSelect = true,
  data = {},
  disabledCheckBox = false
}: Prop) => {
  const [itemSelected, setItemSelected] = useState<boolean>(false);

  useEffect(() => {
    setItemSelected(selected);
  }, [selected]);

  const onClickLi = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      if (data.uncheckable) return;
      if (disabledCheckBox && !itemSelected) return;
      if (!multiple) {
        if (!changeOnSelect && !children.length) {
          onClick(value, label, deep, data);
        } else if (changeOnSelect) {
          onClick(value, label, deep, data);
        }
      } else {
        onChange(!itemSelected, value, data, deep, label);
      }
    },
    [value, onClick, label, multiple, onChange, itemSelected, deep, changeOnSelect, data]
  );

  const onHoverLi = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      onHover(value, children, deep);
    },
    [value, onHover, children, deep]
  );

  const itemStyle = useMemo(() => {
    if (itemWidth) {
      return { width: `${itemWidth}px` };
    }
    return {};
  }, [itemWidth]);

  const renderItem = useCallback(
    label => {
      if (data.uncheckable) {
        return label;
      }
      if (multiple) {
        if (renderNode && typeof renderNode === 'function') {
          return (
            <Checkbox indeterminate={indeterminate} checked={itemSelected} disabled={disabledCheckBox && !itemSelected}>
              {renderNode(label, value, data)}
            </Checkbox>
          );
        }
        return (
          <Checkbox indeterminate={indeterminate} checked={itemSelected} disabled={disabledCheckBox && !itemSelected}>
            {label}
          </Checkbox>
        );
      }
      if (renderNode && typeof renderNode === 'function') {
        return renderNode(label, value, data);
      }
      return label;
    },
    [multiple, renderNode, value, indeterminate, itemSelected, data, disabledCheckBox]
  );

  return (
    <li
      className={`${style.item} ${itemSelected && !multiple ? style.selected : ''} ${active ? style.active : ''} ${
        multiple ? style.multiple : ''
      }`}
      style={itemStyle}
      onFocus={defaultFunc}
      onClick={onClickLi}
      title={label.toString()}
      onMouseOver={onHoverLi}
    >
      <div className={style.multiple_wrap} title={label.toString()}>
        {renderItem(label)}
      </div>
      {children.length ? (
        <span className={style.icon}>
          <RightOutlined />
        </span>
      ) : null}
    </li>
  );
};

export default Item;
